import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Menu, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { cn } from '../lib/utils';

const CATEGORIES = [
  { id: "All", label: "All Categories" },
  { id: "Jewelry", label: "Jewelry" },
  { id: "Pottery", label: "Pottery" },
  { id: "Rugs", label: "Rugs" },
  { id: "Clothing", label: "Clothing" },
  { id: "Decor", label: "Home Decor" }
];

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || "All";
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [categoryParam]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const url = categoryParam && categoryParam !== "All" 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/products?category=${categoryParam}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/products`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products", error);
    } finally {
      setLoading(false);
    }
  };

  const setCategory = (cat) => {
    if (cat === "All") {
      searchParams.delete('category');
      setSearchParams(searchParams);
    } else {
      setSearchParams({ category: cat });
    }
    setIsMobileFiltersOpen(false);
  };

  const CategorySidebar = () => (
    <div className="space-y-2">
      <h3 className="font-bold text-lg mb-4 px-2">Categories</h3>
      <div className="flex flex-col space-y-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            className={cn(
              "text-left px-4 py-3 rounded-lg transition-colors font-medium",
              categoryParam === cat.id 
                ? "bg-primary text-white shadow-md" 
                : "text-stone-600 hover:bg-stone-100"
            )}
          >
            {cat.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Mobile Header with Filter Button */}
      <div className="md:hidden flex gap-4 mb-6">
        <Sheet open={isMobileFiltersOpen} onOpenChange={setIsMobileFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter size={18} /> Filters
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px]">
             <div className="mt-6">
                <CategorySidebar />
             </div>
          </SheetContent>
        </Sheet>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 h-4 w-4" />
          <Input placeholder={t('search')} className="pl-10" />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24 bg-white p-6 rounded-2xl border border-stone-100 shadow-sm">
             <CategorySidebar />
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1">
          <div className="hidden md:flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-stone-900">
              {categoryParam === "All" ? t('marketplace') : categoryParam}
            </h1>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 h-4 w-4" />
              <Input placeholder={t('search')} className="pl-10" />
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
               {[1,2,3,4,5,6].map(i => <div key={i} className="h-80 bg-stone-100 rounded-2xl"></div>)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map(product => (
                <Link key={product.id} to={`/product/${product.id}`} className="group bg-white rounded-2xl overflow-hidden border border-stone-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="aspect-[4/5] overflow-hidden bg-stone-100 relative">
                    <img 
                      src={product.image_url || "https://via.placeholder.com/300?text=No+Image"} 
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                     {product.delivery_option === 'free' && (
                        <span className="absolute top-3 right-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-sm">Free Delivery</span>
                      )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg text-stone-900 truncate mb-1">{product.title}</h3>
                    <p className="text-sm text-stone-500 truncate mb-3">{product.seller_name}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-primary font-bold text-xl">{product.price} DZD</span>
                      <Button size="sm" variant="secondary" className="h-8 px-4 rounded-full">View</Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          
          {products.length === 0 && !loading && (
            <div className="text-center py-20 bg-stone-50 rounded-3xl">
              <p className="text-xl text-stone-500 mb-4">No products found in this category.</p>
              <Button onClick={() => setCategory("All")} variant="outline">Clear Filters</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
